import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef } from 'react';
import { CHECKOUT_SUBPATH } from './routing';

// When state machine changes the step, update URL to match
// This is very important, without changing the URL the new props for the page cannot be fetched.
export const useSyncPathToMachine = (
  routeChanging,
  isAppSession,
  stateKey,
  unmappedStates,
  pet_id,
  setShowPlanSelection,
  setAnimationDone,
) => {
  const router = useRouter();
  const [subpath, slug] = router.query.path ?? [];
  const pathname = slug ? `${subpath}/${slug}` : subpath;

  // Keep a ref to always have the latest stateKey. This prevents a race condition
  // where onRouteChangeComplete fires before React commits the next render, causing
  // syncPathToMachine to use a stale stateKey from the previous closure.
  const stateKeyRef = useRef(stateKey);
  stateKeyRef.current = stateKey;

  const syncPathToMachine = useCallback(() => {
    if (!routeChanging.current) {
      const currentStateKey = stateKeyRef.current;
      const query = isAppSession ? { isAppSession } : {};

      // Prevent duplicate navigation: if user is already on any path and we're attempting
      // to re-navigate to the same path, we should skip the navigation.
      // We check router.asPath to detect the current URL, but only skip navigation if
      // pathname is also undefined or equal to stateKey. This ensures we allow navigation
      // between /onboarding/ and /onboarding-v2/ while preventing duplicate navigation.
      // This fixes an issue where duplicate navigation attempts occur across multiple
      // See ticket: https://ollie.atlassian.net/browse/ES-1679
      const currentPath =
        router.asPath?.split('?')[0]?.replace(/\/$/, '') || '';
      const targetPath = currentStateKey ? `/${currentStateKey}` : '';
      const asPathMatchesTarget =
        targetPath && currentPath.endsWith(targetPath);
      const isAlreadyOnTargetPath =
        asPathMatchesTarget && (pathname === currentStateKey || !pathname);

      // Skip navigation if already on the target path
      if (isAlreadyOnTargetPath) {
        return;
      }
      if (
        currentStateKey &&
        currentStateKey === CHECKOUT_SUBPATH &&
        pathname !== currentStateKey
      )
        router.push({
          pathname: `${currentStateKey}`,
          query,
        });
      else if (
        currentStateKey &&
        pathname !== currentStateKey &&
        !unmappedStates.includes(currentStateKey)
      ) {
        query.pet_id = pet_id;
        router.push({
          pathname: `${currentStateKey}`,
          query,
        });
      }
    }
  }, [pathname, router, isAppSession, pet_id, routeChanging, unmappedStates]);

  useEffect(() => {
    syncPathToMachine();
    const onRouteChangeComplete = () => {
      routeChanging.current = false;
      syncPathToMachine();
      setShowPlanSelection(false);
      setAnimationDone?.(false);
    };
    // Reset routeChanging on error so it never gets stuck at true, which would
    // silently prevent all future syncPathToMachine navigations.
    const onRouteChangeError = () => {
      routeChanging.current = false;
    };

    router.events.on('routeChangeComplete', onRouteChangeComplete);
    router.events.on('routeChangeError', onRouteChangeError);
    return () => {
      router.events.off('routeChangeComplete', onRouteChangeComplete);
      router.events.off('routeChangeError', onRouteChangeError);
    };
  }, [
    syncPathToMachine,
    router,
    routeChanging,
    setShowPlanSelection,
    setAnimationDone,
    stateKey,
  ]);
};
